import React, { useState } from 'react';
import { Row, Col, Card, Statistic, Progress, Alert } from 'antd';
import {
    FileTextOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
    DollarOutlined,
    RiseOutlined,
    BankOutlined,
    TrophyOutlined,
    PercentageOutlined
} from '@ant-design/icons';
import { formatCurrency } from '../../../../shared/utils/formatters';
import { savingsService } from '../../../savings/services/savingsService';
import dayjs from 'dayjs';
import styles from './DebtStats.module.css';

export default function DebtStats({ debts, savings = [] }) {

    const [dismissedAlerts, setDismissedAlerts] = useState({
        overdue: false,
        noSavingsWithDebt: false,
        highDebtRatio: false,
        canPayOffDebt: false,
        recommendation: false
    });

    // Thêm function này
    const dismissAlert = (alertType) => {
        setDismissedAlerts(prev => ({
            ...prev,
            [alertType]: true
        }));
    };

    const calculateDebtStats = () => {
        const totalDebt = debts.reduce((sum, debt) => sum + debt.soTien, 0);
        const totalPaid = debts.reduce((sum, debt) => sum + (debt.soTienDaTra || 0), 0);
        const totalUnpaid = totalDebt - totalPaid;

        // Phân loại chi tiết
        const fullyPaidCount = debts.filter(debt => {
            const paidAmount = debt.soTienDaTra || 0;
            return debt.daTra || (paidAmount >= debt.soTien);
        }).length;

        const partiallyPaidCount = debts.filter(debt => {
            const paidAmount = debt.soTienDaTra || 0;
            const paidPercentage = (paidAmount / debt.soTien) * 100;
            return paidAmount > 0 && paidPercentage < 100 && !debt.daTra;
        }).length;

        const unpaidCount = debts.filter(debt => {
            const paidAmount = debt.soTienDaTra || 0;
            return paidAmount === 0;
        }).length;

        const overdueDebts = debts.filter(debt => {
            const paidAmount = debt.soTienDaTra || 0;
            const isNotFullyPaid = !debt.daTra && (paidAmount < debt.soTien);
            return isNotFullyPaid && dayjs(debt.ngayTra).isBefore(dayjs(), 'day');
        });

        return {
            totalDebt,
            totalPaid,
            totalUnpaid,
            fullyPaidCount,
            partiallyPaidCount,
            unpaidCount,
            overdueCount: overdueDebts.length,
            paymentRate: totalDebt > 0 ? (totalPaid / totalDebt) * 100 : 0,
            totalCount: debts.length
        };
    };

    const calculateSavingsStats = () => {
        const totalSavingsPrincipal = savings.reduce((sum, saving) => sum + saving.soTienGoc, 0);
        const totalSavingsValue = savings.reduce((sum, saving) => {
            if (saving.trangThai === 'withdrawn') return sum;
            const currentValue = savingsService.calculateCurrentValue(saving);
            return sum + currentValue.currentValue;
        }, 0);
        const totalInterestEarned = totalSavingsValue - totalSavingsPrincipal;
        const activeSavingsCount = savings.filter(s => s.trangThai === 'active').length;

        return {
            totalSavingsPrincipal,
            totalSavingsValue,
            totalInterestEarned,
            activeSavingsCount,
            totalSavingsCount: savings.length
        };
    };

    const calculateFinancialRatios = (debtStats, savingsStats) => {
        // Tỷ lệ nợ trên tiết kiệm (Debt-to-Savings Ratio)
        let debtToSavingsRatio = 0;
        let debtToSavingsStatus = 'no-savings';

        if (savingsStats.totalSavingsValue > 0) {
            debtToSavingsRatio = (debtStats.totalUnpaid / savingsStats.totalSavingsValue) * 100;
            debtToSavingsStatus = 'normal';
        } else if (debtStats.totalUnpaid > 0) {
            // Có nợ nhưng không có tiết kiệm
            debtToSavingsRatio = 999; // Giá trị rất cao để thể hiện rủi ro
            debtToSavingsStatus = 'no-savings-with-debt';
        }

        // Tài sản ròng (Net Worth)
        const netWorth = savingsStats.totalSavingsValue - debtStats.totalUnpaid;

        // Khả năng thanh toán nợ bằng tiết kiệm
        const debtCoverageRatio = debtStats.totalUnpaid > 0 ?
            (savingsStats.totalSavingsValue / debtStats.totalUnpaid) * 100 : 100;

        return {
            debtToSavingsRatio,
            debtToSavingsStatus,
            netWorth,
            debtCoverageRatio
        };
    };


    const debtStats = calculateDebtStats();
    const savingsStats = calculateSavingsStats();
    const financialRatios = calculateFinancialRatios(debtStats, savingsStats);

    return (
        <div className={styles.statsContainer}>
            {/* Cảnh báo nợ quá hạn */}
            {debtStats.overdueCount > 0 && !dismissedAlerts.overdue && (
                <Alert
                    message={`⚠️ Bạn có ${debtStats.overdueCount} khoản nợ quá hạn!`}
                    type="error"
                    showIcon
                    closable
                    onClose={() => dismissAlert('overdue')}
                    style={{ marginBottom: '20px' }}
                />
            )}

            {/* Cảnh báo không có tiết kiệm khi có nợ */}
            {financialRatios.debtToSavingsStatus === 'no-savings-with-debt' && !dismissedAlerts.noSavingsWithDebt && (
                <Alert
                    message={`🚨 Bạn có nợ ${formatCurrency(debtStats.totalUnpaid)} nhưng không có tiết kiệm! Rất rủi ro!`}
                    type="error"
                    showIcon
                    closable
                    onClose={() => dismissAlert('noSavingsWithDebt')}
                    style={{ marginBottom: '20px' }}
                />
            )}

            {/* Cảnh báo tỷ lệ nợ cao - chỉ khi có tiết kiệm */}
            {financialRatios.debtToSavingsStatus === 'normal' &&
                financialRatios.debtToSavingsRatio > 80 &&
                !dismissedAlerts.highDebtRatio && (
                    <Alert
                        message={`⚠️ Tỷ lệ nợ/tiết kiệm cao (${financialRatios.debtToSavingsRatio.toFixed(1)}%). Cần tăng cường tiết kiệm hoặc giảm nợ!`}
                        type="warning"
                        showIcon
                        closable
                        onClose={() => dismissAlert('highDebtRatio')}
                        style={{ marginBottom: '20px' }}
                    />
                )}

            {/* Thống kê chính - 4 cards */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card className={styles.statCard}>
                        <Statistic
                            title="Tổng nợ còn lại"
                            value={debtStats.totalUnpaid}
                            valueStyle={{ color: '#ff4d4f' }}
                            prefix={<DollarOutlined />}
                            formatter={(value) => formatCurrency(value)}
                        />
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            Đã trả: {formatCurrency(debtStats.totalPaid)}
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className={styles.statCard}>
                        <Statistic
                            title="Tổng tiết kiệm"
                            value={savingsStats.totalSavingsValue}
                            valueStyle={{ color: '#52c41a' }}
                            prefix={<BankOutlined />}
                            formatter={(value) => formatCurrency(value)}
                        />
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            Lãi: {formatCurrency(savingsStats.totalInterestEarned)}
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className={styles.statCard}>
                        <Statistic
                            title="Tài sản ròng"
                            value={financialRatios.netWorth}
                            valueStyle={{ color: financialRatios.netWorth >= 0 ? '#52c41a' : '#ff4d4f' }}
                            prefix={<TrophyOutlined />}
                            formatter={(value) => formatCurrency(value)}
                        />
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            {financialRatios.netWorth >= 0 ? 'Dương' : 'Âm'}
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className={styles.statCard}>
                        <Statistic
                            title="Tỷ lệ nợ/tiết kiệm"
                            value={financialRatios.debtToSavingsStatus === 'no-savings-with-debt' ? '∞' : financialRatios.debtToSavingsRatio}
                            suffix={financialRatios.debtToSavingsStatus === 'no-savings-with-debt' ? '' : '%'}
                            precision={financialRatios.debtToSavingsStatus === 'no-savings-with-debt' ? 0 : 1}
                            valueStyle={{
                                color: financialRatios.debtToSavingsStatus === 'no-savings-with-debt' ? '#ff4d4f' :
                                    financialRatios.debtToSavingsStatus === 'no-savings' ? '#d9d9d9' :
                                        financialRatios.debtToSavingsRatio > 50 ? '#ff4d4f' :
                                            financialRatios.debtToSavingsRatio > 30 ? '#faad14' : '#52c41a'
                            }}
                            prefix={<PercentageOutlined />}
                        />
                        <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                            {financialRatios.debtToSavingsStatus === 'no-savings-with-debt' ? 'Không có tiết kiệm!' :
                                financialRatios.debtToSavingsStatus === 'no-savings' ? 'Chưa có dữ liệu' :
                                    financialRatios.debtToSavingsRatio <= 30 ? 'Tuyệt vời' :
                                        financialRatios.debtToSavingsRatio <= 50 ? 'Khá tốt' :
                                            financialRatios.debtToSavingsRatio <= 80 ? 'Cần cải thiện' : 'Rủi ro cao'}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Tổng quan nợ - Bố cục lại */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col span={24}>
                    <Card className={styles.summaryCard} title="📊 Tổng quan nợ">
                        <Row gutter={[16, 16]}>
                            {/* Hàng đầu - Thống kê cơ bản */}
                            <Col xs={24} sm={12} md={6}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Tổng số nợ"
                                        value={debtStats.totalCount}
                                        prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
                                        valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Đã trả hết"
                                        value={debtStats.fullyPaidCount}
                                        prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                        valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                                    />
                                    <Progress
                                        percent={debtStats.totalCount > 0 ? Math.round((debtStats.fullyPaidCount / debtStats.totalCount) * 100) : 0}
                                        strokeColor="#52c41a"
                                        size="small"
                                        showInfo={false}
                                        style={{ marginTop: 8 }}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Đã trả một phần"
                                        value={debtStats.partiallyPaidCount}
                                        prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                                        valueStyle={{ color: '#faad14', fontSize: '20px' }}
                                    />
                                    <Progress
                                        percent={debtStats.totalCount > 0 ? Math.round((debtStats.partiallyPaidCount / debtStats.totalCount) * 100) : 0}
                                        strokeColor="#faad14"
                                        size="small"
                                        showInfo={false}
                                        style={{ marginTop: 8 }}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Chưa trả"
                                        value={debtStats.unpaidCount}
                                        prefix={<ExclamationCircleOutlined style={{ color: '#fa8c16' }} />}
                                        valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
                                    />
                                    <Progress
                                        percent={debtStats.totalCount > 0 ? Math.round((debtStats.unpaidCount / debtStats.totalCount) * 100) : 0}
                                        strokeColor="#fa8c16"
                                        size="small"
                                        showInfo={false}
                                        style={{ marginTop: 8 }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Hàng thứ hai - Thống kê tiết kiệm và tỷ lệ */}
                        <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                            <Col xs={24} sm={8} md={4}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Tổng tiết kiệm"
                                        value={savingsStats.totalSavingsCount}
                                        prefix={<BankOutlined style={{ color: '#722ed1' }} />}
                                        valueStyle={{ color: '#722ed1', fontSize: '18px' }}
                                    />
                                    <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                                        {savingsStats.activeSavingsCount} đang gửi
                                    </div>
                                </Card>
                            </Col>

                            <Col xs={24} sm={8} md={5}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Khả năng thanh toán"
                                        value={financialRatios.debtCoverageRatio}
                                        suffix="%"
                                        precision={1}
                                        valueStyle={{
                                            color: financialRatios.debtCoverageRatio >= 100 ? '#52c41a' :
                                                financialRatios.debtCoverageRatio >= 50 ? '#faad14' : '#ff4d4f'
                                        }}
                                        prefix={<CheckCircleOutlined />}
                                    />
                                    <Progress
                                        percent={Math.min(100, financialRatios.debtCoverageRatio)}
                                        strokeColor={
                                            financialRatios.debtCoverageRatio >= 100 ? '#52c41a' :
                                                financialRatios.debtCoverageRatio >= 50 ? '#faad14' : '#ff4d4f'
                                        }
                                        size="small"
                                        style={{ marginTop: 8 }}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={8} md={5}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Tỷ lệ trả nợ"
                                        value={debtStats.paymentRate}
                                        suffix="%"
                                        precision={1}
                                        valueStyle={{ color: debtStats.paymentRate > 70 ? '#52c41a' : '#faad14' }}
                                        prefix={<RiseOutlined />}
                                    />
                                    <Progress
                                        percent={Math.round(debtStats.paymentRate)}
                                        size="small"
                                        style={{ marginTop: 8 }}
                                        strokeColor={debtStats.paymentRate > 70 ? '#52c41a' : '#faad14'}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={5}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Tiền gốc TK"
                                        value={savingsStats.totalSavingsPrincipal}
                                        formatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                                        prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
                                        valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={5}>
                                <Card size="small" className={styles.statCard}>
                                    <Statistic
                                        title="Lãi tích lũy"
                                        value={savingsStats.totalInterestEarned}
                                        formatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                                        prefix={<RiseOutlined style={{ color: '#52c41a' }} />}
                                        valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Cảnh báo và khuyến nghị */}
                        <Row style={{ marginTop: 16 }}>
                            <Col span={24}>
                                {debtStats.overdueCount > 0 && !dismissedAlerts.overdue && (
                                    <Alert
                                        message={`⚠️ Có ${debtStats.overdueCount} khoản nợ quá hạn cần xử lý`}
                                        type="warning"
                                        showIcon
                                        closable
                                        onClose={() => dismissAlert('overdue')}
                                        style={{ marginBottom: 8 }}
                                    />
                                )}

                                {financialRatios.debtCoverageRatio >= 100 && !dismissedAlerts.canPayOffDebt && (
                                    <Alert
                                        message={`✅ Bạn có đủ tiết kiệm để trả hết nợ! Cân nhắc thanh toán sớm để tiết kiệm lãi.`}
                                        type="success"
                                        showIcon
                                        closable
                                        onClose={() => dismissAlert('canPayOffDebt')}
                                        style={{ marginBottom: 8 }}
                                    />
                                )}

                                {financialRatios.debtToSavingsStatus === 'no-savings-with-debt' && !dismissedAlerts.recommendation && (
                                    <Alert
                                        message={`💡 Khuyến nghị khẩn cấp: Bắt đầu tiết kiệm ngay để có khả năng trả nợ và ứng phó khẩn cấp`}
                                        type="error"
                                        showIcon
                                        closable
                                        onClose={() => dismissAlert('recommendation')}
                                        style={{ marginBottom: 8 }}
                                    />
                                )}

                                {financialRatios.debtToSavingsStatus === 'normal' &&
                                    financialRatios.debtToSavingsRatio > 80 &&
                                    !dismissedAlerts.recommendation && (
                                        <Alert
                                            message={`💡 Khuyến nghị: Tăng cường tiết kiệm hoặc ưu tiên trả nợ để cải thiện tỷ lệ nợ/tiết kiệm`}
                                            type="info"
                                            showIcon
                                            closable
                                            onClose={() => dismissAlert('recommendation')}
                                            style={{ marginBottom: 8 }}
                                        />
                                    )}
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
